import { useState, useRef, useCallback, useEffect } from 'react';
import { Crosshair, RotateCcw, Eye } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PrintZoneEditor({ view, productImages, onSave, productFinishes = [] }) {
  // Active finish: null = default print_zone, otherwise finish label string
  const [activeFinish, setActiveFinish] = useState(null);

  const getInitialZone = (finishLabel) => {
    if (finishLabel && view.finish_zones?.[finishLabel]) {
      return view.finish_zones[finishLabel];
    }
    return view.print_zone || { x: 10, y: 10, width: 30, height: 30 };
  };

  const [zone, setZone] = useState(getInitialZone(null));
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const canvasRef = useRef(null);

  const viewImage = productImages?.[view.id] || view.image;

  // When switching finish, load the corresponding zone
  const handleFinishSwitch = (finishLabel) => {
    setActiveFinish(finishLabel);
    setZone(getInitialZone(finishLabel));
  };

  const getPercent = useCallback((e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: Math.max(0, Math.min(100, ((cx - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((cy - rect.top) / rect.height) * 100)),
    };
  }, []);

  const handleCanvasDown = useCallback((e) => {
    const pos = getPercent(e);
    const inZone = pos.x >= zone.x && pos.x <= zone.x + zone.width && pos.y >= zone.y && pos.y <= zone.y + zone.height;
    if (inZone) {
      setDragging(true);
      setDragStart({ mx: pos.x, my: pos.y, ox: zone.x, oy: zone.y });
    } else {
      setDrawing(true);
      setDrawStart(pos);
      setZone({ x: pos.x, y: pos.y, width: 0, height: 0 });
    }
  }, [getPercent, zone]);

  const handleCanvasMove = useCallback((e) => {
    if (drawing && drawStart) {
      e.preventDefault();
      const pos = getPercent(e);
      const x = Math.min(drawStart.x, pos.x);
      const y = Math.min(drawStart.y, pos.y);
      const w = Math.abs(pos.x - drawStart.x);
      const h = Math.abs(pos.y - drawStart.y);
      setZone({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, width: Math.round(w * 10) / 10, height: Math.round(h * 10) / 10 });
    } else if (dragging && dragStart) {
      e.preventDefault();
      const pos = getPercent(e);
      const dx = pos.x - dragStart.mx;
      const dy = pos.y - dragStart.my;
      const nx = Math.max(0, Math.min(100 - zone.width, dragStart.ox + dx));
      const ny = Math.max(0, Math.min(100 - zone.height, dragStart.oy + dy));
      setZone(prev => ({ ...prev, x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 }));
    }
  }, [drawing, drawStart, dragging, dragStart, zone.width, zone.height, getPercent]);

  const handleCanvasUp = useCallback(() => {
    setDrawing(false);
    setDrawStart(null);
    setDragging(false);
    setDragStart(null);
  }, []);

  useEffect(() => {
    if (drawing || dragging) {
      const move = (e) => handleCanvasMove(e);
      const up = () => handleCanvasUp();
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
      window.addEventListener('touchmove', move, { passive: false });
      window.addEventListener('touchend', up);
      return () => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        window.removeEventListener('touchmove', move);
        window.removeEventListener('touchend', up);
      };
    }
  }, [drawing, dragging, handleCanvasMove, handleCanvasUp]);

  const handleSlider = (key, val) => {
    const v = parseFloat(val);
    if (isNaN(v)) return;
    const updated = { ...zone, [key]: v };
    if (updated.x + updated.width > 100) updated.width = 100 - updated.x;
    if (updated.y + updated.height > 100) updated.height = 100 - updated.y;
    setZone(updated);
  };

  const handleReset = () => setZone({ x: 10, y: 10, width: 30, height: 30 });

  // Copy the default print_zone into the current finish zone
  const handleCopyFromDefault = () => {
    const defaultZone = view.print_zone || { x: 10, y: 10, width: 30, height: 30 };
    setZone({ ...defaultZone });
  };

  // Copy a specific finish zone into the current zone
  const handleCopyFromFinish = (finishLabel) => {
    const fz = view.finish_zones?.[finishLabel];
    if (fz) setZone({ ...fz });
  };

  const handleSave = () => {
    if (activeFinish) {
      const updatedFinishZones = { ...(view.finish_zones || {}), [activeFinish]: zone };
      onSave({ ...view, finish_zones: updatedFinishZones });
    } else {
      onSave({ ...view, print_zone: zone });
    }
  };

  // Non-default finishes (skip the first/default finish)
  const alternateFinishes = productFinishes.filter((_, i) => i > 0);
  const hasFinishOptions = alternateFinishes.length > 0;

  // Zone colour per finish for visual distinction
  const zoneColor = activeFinish ? '#F59E0B' : '#00E5FF';

  // Build "copy from" options for the current editing mode
  const copyOptions = [];
  if (activeFinish) {
    // Editing a finish zone — offer to copy from the default
    copyOptions.push({ label: productFinishes[0]?.label || 'Default', action: handleCopyFromDefault });
    // Also offer other alternate finishes if they have zones set
    alternateFinishes.forEach(f => {
      if (f.label !== activeFinish && view.finish_zones?.[f.label]) {
        copyOptions.push({ label: f.label, action: () => handleCopyFromFinish(f.label) });
      }
    });
  } else if (hasFinishOptions) {
    // Editing default zone — offer to copy from any finish zone that exists
    alternateFinishes.forEach(f => {
      if (view.finish_zones?.[f.label]) {
        copyOptions.push({ label: f.label, action: () => handleCopyFromFinish(f.label) });
      }
    });
  }

  return (
    <div className="space-y-4" data-testid="print-zone-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crosshair className="w-4 h-4" style={{ color: zoneColor }} />
          <h3 className="text-sm font-semibold text-white">
            {view.name} — {activeFinish ? `${activeFinish} Zone` : 'Print Zone'}
          </h3>
        </div>
        <button onClick={handleReset} className="text-xs text-slate-500 hover:text-white flex items-center gap-1 transition-colors">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      {/* Finish Switcher */}
      {hasFinishOptions && (
        <div className="flex gap-1.5 flex-wrap" data-testid="zone-finish-switcher">
          <button
            onClick={() => handleFinishSwitch(null)}
            data-testid="zone-finish-default"
            className={`text-[10px] font-medium px-3 py-1.5 rounded-lg border transition-colors ${
              activeFinish === null
                ? 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/10'
                : 'border-white/10 text-slate-500 hover:text-white hover:border-white/20'
            }`}
          >
            {productFinishes[0]?.label || 'Default'}
          </button>
          {alternateFinishes.map((f) => (
            <button
              key={f.label}
              onClick={() => handleFinishSwitch(f.label)}
              data-testid={`zone-finish-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`text-[10px] font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                activeFinish === f.label
                  ? 'border-amber-400 text-amber-400 bg-amber-400/10'
                  : 'border-white/10 text-slate-500 hover:text-white hover:border-white/20'
              }`}
            >
              {f.label}
            </button>
          ))}
          {activeFinish && (
            <span className="text-[9px] text-amber-400/60 self-center ml-1">Editing {activeFinish} zone</span>
          )}
        </div>
      )}

      {/* Copy Zone (Same as...) */}
      {copyOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="zone-copy-options">
          <span className="text-[10px] text-slate-600">Same as:</span>
          {copyOptions.map((opt) => (
            <button
              key={opt.label}
              onClick={opt.action}
              data-testid={`zone-copy-${opt.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-[10px] font-medium px-2.5 py-1 rounded-lg border border-dashed border-white/15 text-slate-400 hover:text-white hover:border-[#00E5FF]/40 hover:bg-[#00E5FF]/5 transition-colors"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Visual Preview */}
      <div className="relative">
        <div
          ref={canvasRef}
          className="aspect-square bg-[#0A0A0A] rounded-xl border border-white/10 relative overflow-hidden cursor-crosshair select-none touch-none"
          onMouseDown={handleCanvasDown}
          onTouchStart={handleCanvasDown}
          data-testid="zone-canvas"
        >
          {viewImage ? (
            <img src={`${API}/api/files/${viewImage}`} alt={view.name} className="w-full h-full object-contain p-[8%] pointer-events-none opacity-60" draggable={false} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Eye className="w-12 h-12 text-slate-800" />
            </div>
          )}

          {/* Print zone overlay */}
          {zone.width > 0 && zone.height > 0 && (
            <div
              className={`absolute border-2 rounded-sm transition-colors ${dragging ? 'bg-opacity-10' : 'bg-opacity-5'} cursor-grab active:cursor-grabbing`}
              style={{
                left: `${zone.x}%`, top: `${zone.y}%`, width: `${zone.width}%`, height: `${zone.height}%`,
                borderColor: dragging ? zoneColor : `${zoneColor}99`,
                backgroundColor: `${zoneColor}${dragging ? '1A' : '0D'}`,
              }}
              data-testid="zone-preview"
            >
              <span className="absolute -top-5 left-0 text-[9px] font-mono whitespace-nowrap" style={{ color: zoneColor }}>
                {Math.round(zone.width)}% x {Math.round(zone.height)}%
              </span>
              <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full" style={{ backgroundColor: zoneColor }} />
              <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: zoneColor }} />
              <div className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full" style={{ backgroundColor: zoneColor }} />
              <div className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full" style={{ backgroundColor: zoneColor }} />
            </div>
          )}
        </div>
        <p className="text-[10px] text-slate-600 mt-1.5">Click and drag to draw zone . Drag zone to reposition</p>
      </div>

      {/* Numeric Controls */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { key: 'x', label: 'X Position', max: 100 },
          { key: 'y', label: 'Y Position', max: 100 },
          { key: 'width', label: 'Width', max: 100 },
          { key: 'height', label: 'Height', max: 100 },
        ].map(({ key, label, max }) => (
          <div key={key} data-testid={`zone-slider-${key}`}>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-600">{label}</label>
              <input
                type="number" min="0" max={max} step="0.5" value={zone[key]}
                onChange={e => handleSlider(key, e.target.value)}
                data-testid={`zone-input-${key}`}
                className="w-16 text-right text-xs font-mono bg-white/5 border border-white/10 rounded-md px-2 py-0.5 focus:outline-none focus:border-[#00E5FF]/50"
                style={{ color: zoneColor }}
              />
            </div>
            <input
              type="range" min="0" max={max} step="0.5" value={zone[key]}
              onChange={e => handleSlider(key, e.target.value)}
              className="w-full h-1.5 rounded-full cursor-pointer"
              style={{ accentColor: zoneColor }}
            />
          </div>
        ))}
      </div>

      {/* Save */}
      <button onClick={handleSave} data-testid="save-zone-btn"
        className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
          activeFinish
            ? 'bg-amber-400 text-[#0A0A0A] hover:bg-amber-300'
            : 'bg-[#00E5FF] text-[#0A0A0A] hover:bg-white'
        }`}>
        Save {activeFinish ? `${activeFinish} Zone` : 'Print Zone'}
      </button>
    </div>
  );
}
